import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Ban, Check, FileText, PencilIcon, RefreshCw, Trash2, TriangleAlert, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { usePermissions } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { Eyebrow, ErrorState, LoadingState } from "@/components/PageUi";
import {
  ExpenseApprovalPolicyFields,
  useExpensePolicyReferenceData,
  type ExpensePolicyReferenceData,
} from "@/components/ExpenseApprovalPolicyFields";
import {
  POLICY_DOCUMENT_POLL_MS,
  PolicyDocumentStatusBadge,
  type ExpensePolicyDocumentResponse,
} from "@/components/ExpensePolicyDocumentsPanel";
import {
  clausePageLabel,
  draftFromProposal,
  expenseActionBadgeClass,
  expenseActionLabel,
  expenseConditionSummary,
  toUpsertRequest,
  validateDraft,
  versionLabel,
  type ExpensePolicyDraft,
  type ExpensePolicyProposalResponse,
} from "@/lib/expense-approval-policy-utils";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-public";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ExpensePolicyProposalChange = components["schemas"]["ExpensePolicyProposalChange"];
type ConfirmedExpensePolicyProposal = components["schemas"]["ConfirmedExpensePolicyProposal"];

/** One proposal as the reviewer is shaping it: the compiled original, their edits, and whether it is kept. */
interface ProposalState {
  proposal: ExpensePolicyProposalResponse;
  /** The proposal as compiled, to tell whether the reviewer has changed its rule. */
  original: ExpensePolicyDraft;
  draft: ExpensePolicyDraft;
  included: boolean;
  editing: boolean;
  edited: boolean;
}

const CHANGE_META: Record<ExpensePolicyProposalChange, { label: string; className?: string }> = {
  Added: { label: "New", className: "border-accent-blue/30 bg-accent-blue/10 text-accent-blue" },
  Changed: { label: "Changed", className: "border-amber-500/30 bg-amber-500/15 text-amber-600" },
  Unchanged: { label: "Unchanged" },
};

function ChangeBadge({ change }: { change?: ExpensePolicyProposalChange }) {
  const meta = change ? CHANGE_META[change] : undefined;
  if (!meta) return null;
  return meta.className ? (
    <Badge variant="outline" className={cn("text-xs font-normal", meta.className)}>
      {meta.label}
    </Badge>
  ) : (
    <Badge variant="secondary" className="text-xs font-normal">
      {meta.label}
    </Badge>
  );
}

function ClauseQuote({ clause }: { clause: { quote?: string; page?: number | string | null } }) {
  const page = clausePageLabel(clause);
  return (
    <blockquote className="border-l-2 border-accent-blue/50 pl-3 text-sm text-pretty text-muted-foreground italic">
      &ldquo;{clause.quote}&rdquo;
      {page ? <span className="ml-1.5 text-xs not-italic">({page})</span> : null}
    </blockquote>
  );
}

function PolicyReadout({ draft, referenceData: ref }: { draft: ExpensePolicyDraft; referenceData: ExpensePolicyReferenceData }) {
  const conditions = expenseConditionSummary(
    {
      amountGte: draft.amountGte || null,
      amountLte: draft.amountLte || null,
      currency: draft.currency || null,
      mccCodes: draft.mccCodes,
      receiptCondition: draft.receiptCondition,
      personalSpend: draft.personalSpend,
    },
    ref.categoryLabel,
  );
  const scope = [
    ...draft.merchantIds.map((id) => ref.merchantName(id)),
    ...draft.cardConnectionIds.map((id) => ref.cardName(id)),
  ];
  const approvers = draft.approverUserIds.map((id) => ref.userLabel(id));
  return (
    <dl className="grid grid-cols-[88px_1fr] gap-x-3 gap-y-1.5 text-sm md:grid-cols-[88px_1fr_88px_1fr]">
      <dt className="text-muted-foreground">Matches</dt>
      <dd>{conditions}</dd>
      {scope.length ? (
        <>
          <dt className="text-muted-foreground">Only for</dt>
          <dd>{scope.join(", ")}</dd>
        </>
      ) : null}
      <dt className="text-muted-foreground">Action</dt>
      <dd>
        <Badge className={`text-xs font-normal ${expenseActionBadgeClass(draft.action)}`}>
          {expenseActionLabel(draft.action)}
        </Badge>
      </dd>
      {draft.action === "RequireApproval" ? (
        <>
          <dt className="text-muted-foreground">Approvers</dt>
          <dd>{approvers.length ? approvers.join(", ") : "Anyone who can approve"}</dd>
        </>
      ) : null}
      <dt className="text-muted-foreground">Priority</dt>
      <dd className="font-mono text-xs tabular-nums leading-5">{draft.priority}</dd>
    </dl>
  );
}

function ProposalCard({
  index,
  state,
  editable,
  referenceData,
  onChange,
}: {
  index: number;
  state: ProposalState;
  editable: boolean;
  referenceData: ExpensePolicyReferenceData;
  onChange: (next: ProposalState) => void;
}) {
  const { proposal, draft, included, editing, edited } = state;
  const notes = proposal.notes ?? [];
  return (
    <Card className={cn("gap-0 py-0", !included && "opacity-60")}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="font-semibold">{draft.name || "Untitled policy"}</span>
            <ChangeBadge change={proposal.change} />
            {edited ? (
              <Badge variant="secondary" className="text-xs font-normal">
                Edited
              </Badge>
            ) : null}
            {!included ? (
              <Badge variant="outline" className="text-xs font-normal">
                Left out
              </Badge>
            ) : null}
          </div>
          {editable ? (
            <div className="flex shrink-0 gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={!included}
                onClick={() => onChange({ ...state, editing: !editing })}
              >
                <PencilIcon className="size-3.5" /> {editing ? "Done editing" : "Edit"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onChange({ ...state, included: !included, editing: false })}
              >
                {included ? (
                  <>
                    <Ban className="size-3.5" /> Leave out
                  </>
                ) : (
                  <>
                    <Undo2 className="size-3.5" /> Include
                  </>
                )}
              </Button>
            </div>
          ) : null}
        </div>
        <ClauseQuote clause={proposal.clause} />
        <PolicyReadout draft={draft} referenceData={referenceData} />
        {isBlocked(state) ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
            <p className="flex items-center gap-1.5 font-medium text-destructive">
              <TriangleAlert className="size-3.5" /> Fix before confirming
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
              {(proposal.unresolved ?? []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Confirmed as it is, this would apply to more spend than the document says. Edit it, or leave it out.
            </p>
          </div>
        ) : null}
        {notes.length > 0 ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm">
            <p className="flex items-center gap-1.5 font-medium text-amber-600">
              <TriangleAlert className="size-3.5" /> Check before confirming
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
              {notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {editable && editing && included ? (
          <div className="border-t pt-4">
            <ExpenseApprovalPolicyFields
              draft={draft}
              onChange={(next) => onChange({ ...state, draft: next, edited: true })}
              referenceData={referenceData}
              idPrefix={`proposal-${index}`}
              hideActive
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

const sameIds = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

/** Whether the reviewer changed what the proposal matches or does (not just its name or priority). */
function ruleChanged({ original: a, draft: b }: ProposalState): boolean {
  return (
    a.action !== b.action ||
    a.amountGte.trim() !== b.amountGte.trim() ||
    a.amountLte.trim() !== b.amountLte.trim() ||
    a.currency.trim().toUpperCase() !== b.currency.trim().toUpperCase() ||
    !sameIds(a.mccCodes, b.mccCodes) ||
    !sameIds(a.merchantIds, b.merchantIds) ||
    !sameIds(a.cardConnectionIds, b.cardConnectionIds) ||
    !sameIds(a.approverUserIds, b.approverUserIds) ||
    a.receiptCondition !== b.receiptCondition ||
    a.personalSpend !== b.personalSpend
  );
}

/** A kept proposal naming something unmatched would apply a broader rule than the document says, so it blocks. */
function isBlocked(state: ProposalState): boolean {
  return state.included && (state.proposal.unresolved?.length ?? 0) > 0 && !ruleChanged(state);
}

function ConfirmSummary({ kept, removedCount, blockedCount }: { kept: ProposalState[]; removedCount: number; blockedCount: number }) {
  const added = kept.filter((k) => k.proposal.change !== "Changed" && k.proposal.change !== "Unchanged").length;
  const changed = kept.filter((k) => k.proposal.change === "Changed").length;
  const attention = kept.filter((k) => (k.proposal.notes?.length ?? 0) > 0 && !k.edited).length;
  const parts = [
    added ? `${added} new` : null,
    changed ? `${changed} changed` : null,
    removedCount ? `${removedCount} removed` : null,
  ].filter(Boolean);
  if (blockedCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-destructive">
        <TriangleAlert className="size-3.5" />
        {blockedCount === 1 ? "1 proposal must be fixed" : `${blockedCount} proposals must be fixed`} or left out
        before confirming.
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-x-2">
      <span>{parts.length ? `Confirming applies ${parts.join(", ")}.` : "Nothing to apply."}</span>
      {attention > 0 ? (
        <span className="inline-flex items-center gap-1 text-amber-600">
          <TriangleAlert className="size-3.5" />
          {attention} {attention === 1 ? "needs" : "need"} a look
        </span>
      ) : null}
    </span>
  );
}

export interface ExpensePolicyDocumentPageProps {
  documentId: string;
  onBack?: () => void;
  onConfirmed?: () => void;
}

/** Nothing changes silently: unenforceable clauses, and policies confirming would delete, are listed. */
export function ExpensePolicyDocumentPage({ documentId, onBack, onConfirmed }: ExpensePolicyDocumentPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canConfirm = can("manageExpensePolicies");
  const [doc, setDoc] = useState<ExpensePolicyDocumentResponse | null>(null);
  const [proposals, setProposals] = useState<ProposalState[]>([]);
  // The members and merchants the proposals name (as edited), so any beyond the first page are fetched by id.
  const referencedIds = useMemo(
    () => ({
      userIds: proposals.flatMap((p) => p.draft.approverUserIds),
      merchantIds: proposals.flatMap((p) => p.draft.merchantIds),
    }),
    [proposals],
  );
  const referenceData = useExpensePolicyReferenceData(referencedIds);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchDocument = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      const { data, error: apiError } = await api.GET("/v1/expenses/policy-documents/{id}", {
        params: { path: { id: documentId } },
      });
      if (apiError || !data) {
        if (!quiet) setError(apiErrorMessage(apiError, "Couldn't load the policy document."));
      } else {
        setDoc(data);
      }
      if (!quiet) setLoading(false);
    },
    [api, documentId],
  );

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  // Re-seed the reviewer's working copy whenever the compiled proposals change (first load, a finished
  // compile, a retry) - not on every poll.
  const status = doc?.status;
  const seedKey = doc ? `${doc.id}:${doc.status}:${doc.compiledAt ?? ""}` : "";
  useEffect(() => {
    setProposals(
      (doc?.proposals ?? []).map((proposal, i) => ({
        proposal,
        original: draftFromProposal(proposal, i),
        draft: draftFromProposal(proposal, i),
        included: true,
        editing: false,
        edited: false,
      })),
    );
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  useEffect(() => {
    if (status !== "Compiling") return;
    const id = setInterval(() => void fetchDocument({ quiet: true }), POLICY_DOCUMENT_POLL_MS);
    return () => clearInterval(id);
  }, [status, fetchDocument]);

  async function retry() {
    setRetrying(true);
    const { error: apiError } = await api.POST("/v1/expenses/policy-documents/{id}/retry", {
      params: { path: { id: documentId } },
    });
    setRetrying(false);
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Couldn't retry reading the document."));
      return;
    }
    toast.success("Reading the document again.");
    await fetchDocument({ quiet: true });
  }

  const kept = proposals.filter((p) => p.included);
  const blockedCount = proposals.filter(isBlocked).length;
  // Only send the list when the reviewer changed something; otherwise the proposals are confirmed as compiled.
  const reviewed = proposals.some((p) => p.edited || !p.included);

  function openConfirm() {
    if (blockedCount > 0) return;
    for (const p of kept) {
      const problem = validateDraft(p.draft);
      if (problem) {
        setFormError(`${p.draft.name || "A policy"}: ${problem}`);
        return;
      }
    }
    setFormError(null);
    setConfirmOpen(true);
  }

  async function confirm() {
    setConfirmOpen(false);
    setConfirming(true);
    const policies: ConfirmedExpensePolicyProposal[] = kept.map((p) => ({
      ...toUpsertRequest(p.draft),
      active: true,
      clause: { quote: p.proposal.clause.quote, page: p.proposal.clause.page ?? null },
    }));
    const { error: apiError } = await api.POST("/v1/expenses/policy-documents/{id}/confirm", {
      params: { path: { id: documentId } },
      ...(reviewed ? { body: { policies } } : {}),
    });
    setConfirming(false);
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Couldn't confirm the policy document."));
      return;
    }
    toast.success("Confirmed. These policies now apply to expenses.");
    await fetchDocument();
    onConfirmed?.();
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState label="Loading policy document..." />
      </PageContainer>
    );
  }

  if (error || !doc) {
    return (
      <PageContainer>
        {onBack ? <BackLink onClick={onBack}>Back to approval policies</BackLink> : null}
        <ErrorState message={error ?? "That policy document could not be found."} onRetry={() => fetchDocument()} />
      </PageContainer>
    );
  }

  const ready = doc.status === "ReadyForReview";
  const editable = ready && canConfirm;
  const unenforceable = doc.unenforceableClauses ?? [];
  // Confirming deletes current document-sourced policies no kept proposal restates; leaving one out deletes its
  // policy too. Proposals pair with policies by quoted clause, so renaming doesn't.
  const removed: { id: string; name: string; version?: number }[] = [
    ...(doc.removedPolicies ?? []).map((p) => ({ id: p.id!, name: p.name, version: p.version })),
    ...proposals
      .filter((p) => p.proposal.existingPolicyId && !p.included)
      .map((p) => ({ id: p.proposal.existingPolicyId!, name: p.proposal.name })),
  ];

  return (
    <PageContainer>
      {onBack ? <BackLink onClick={onBack}>Back to approval policies</BackLink> : null}

      <PageHeader
        eyebrow="Expenses"
        title={`Expense policy ${versionLabel(doc.version)}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-foreground">{doc.fileName}</span>
            <span aria-hidden>·</span>
            <span>Uploaded {formatDateTime(doc.createdAt)}</span>
            {doc.pageCount ? (
              <>
                <span aria-hidden>·</span>
                <span>
                  {doc.pageCount} {Number(doc.pageCount) === 1 ? "page" : "pages"}
                </span>
              </>
            ) : null}
            {doc.confirmedAt ? (
              <>
                <span aria-hidden>·</span>
                <span>Confirmed {formatDateTime(doc.confirmedAt)}</span>
              </>
            ) : null}
          </span>
        }
        actions={<PolicyDocumentStatusBadge status={doc.status} />}
      />

      {doc.status === "Compiling" ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border bg-card/30 px-6 py-14 text-center text-sm">
          <Spinner className="size-5 text-muted-foreground" />
          <p className="font-semibold">Reading your policy</p>
          <p className="max-w-md text-muted-foreground">
            Dolfin is turning each clause into a proposed policy. This usually takes 10 to 30 seconds, and this page
            updates on its own.
          </p>
        </div>
      ) : doc.status === "Failed" ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-6 py-12 text-center text-sm">
          <AlertTriangle className="size-5 text-destructive" />
          <p className="font-semibold">This document could not be read into policies</p>
          {doc.failureReason ? <p className="max-w-md text-muted-foreground">{doc.failureReason}</p> : null}
          <Button variant="outline" size="sm" disabled={retrying} onClick={retry}>
            {retrying ? <Spinner className="size-3.5" /> : <RefreshCw className="size-3.5" />}
            Retry
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {doc.status === "Confirmed" ? (
            <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm">
              This version is in force. Its policies are on the approval policies page, where they can be edited.
            </p>
          ) : doc.status === "Superseded" ? (
            <p className="rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              A newer version of the policy replaced this one. Shown for reference.
            </p>
          ) : ready && !canConfirm ? (
            <p className="rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              Waiting for an admin to review and confirm. Nothing applies until then.
            </p>
          ) : ready ? (
            <p className="rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              Check each proposed policy against the clause it came from. Edit anything that is not right, or leave
              it out. Nothing applies until you confirm.
            </p>
          ) : null}

          <section className="space-y-3">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold">
                Proposed policies <span className="font-normal text-muted-foreground">({proposals.length})</span>
              </h2>
            </div>
            {proposals.length === 0 ? (
              <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                No enforceable rules were found in this document.
              </p>
            ) : (
              proposals.map((state, i) => (
                <ProposalCard
                  key={i}
                  index={i}
                  state={state}
                  editable={editable}
                  referenceData={referenceData}
                  onChange={(next) => setProposals((prev) => prev.map((p, j) => (j === i ? next : p)))}
                />
              ))
            )}
          </section>

          {unenforceable.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold">
                Not enforced <span className="font-normal text-muted-foreground">({unenforceable.length})</span>
              </h2>
              <p className="text-sm text-muted-foreground">
                These clauses cannot be checked automatically against card spend. They still apply to your team,
                but Dolfin will not act on them.
              </p>
              {unenforceable.map((clause, i) => (
                <Card key={i} className="gap-0 py-0">
                  <CardContent className="grid gap-4 p-4 md:grid-cols-2">
                    <ClauseQuote clause={clause} />
                    <div>
                      <Eyebrow className="mb-1.5">Why not</Eyebrow>
                      <p className="text-sm text-muted-foreground">{clause.reason}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </section>
          ) : null}

          {ready && removed.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold">
                Will be removed <span className="font-normal text-muted-foreground">({removed.length})</span>
              </h2>
              <p className="text-sm text-muted-foreground">
                These current policies came from an earlier version of the document. This version does not include
                them, or you left them out, so confirming deletes them.
              </p>
              <div className="divide-y rounded-lg border border-destructive/30 bg-destructive/5">
                {removed.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <Trash2 className="size-3.5 text-destructive" />
                    <span className="font-medium">{p.name}</span>
                    {p.version != null ? (
                      <Badge variant="secondary" className="px-1.5 font-mono text-[10px] font-normal">
                        {versionLabel(p.version)}
                      </Badge>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {editable ? (
            <div className="sticky bottom-0 -mx-4 flex flex-col gap-2 border-t bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="text-sm text-muted-foreground">
                {formError ? (
                  <span className="text-destructive">{formError}</span>
                ) : (
                  <ConfirmSummary kept={kept} removedCount={removed.length} blockedCount={blockedCount} />
                )}
              </div>
              <Button disabled={confirming || blockedCount > 0} onClick={openConfirm}>
                {confirming ? <Spinner className="size-4" /> : <Check className="size-4" />}
                Confirm policies
              </Button>
            </div>
          ) : null}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm {versionLabel(doc.version)} of your expense policy?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  {kept.length === 1 ? "This policy" : `These ${kept.length} policies`} will start applying to
                  expenses straight away: matching spend will be auto-approved, held for approval or flagged as
                  set out above.
                </p>
                {removed.length > 0 ? (
                  <p>
                    {removed.length === 1 ? "1 policy" : `${removed.length} policies`} from the previous version will
                    be deleted.
                  </p>
                ) : null}
                {unenforceable.length > 0 ? (
                  <p className="flex items-start gap-1.5">
                    <FileText className="mt-0.5 size-3.5 shrink-0" />
                    {unenforceable.length === 1 ? "1 clause is" : `${unenforceable.length} clauses are`} not enforced
                    automatically.
                  </p>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirm}>Confirm and apply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
